import type {
  Bonus,
  Differentiator,
  Faq,
  InsideImage,
  Module,
  PainPoint,
  PopupSale,
  Testimonial,
} from '../types/content'

export const CHECKOUT_URL = 'https://kitchen-equipment-diagnostic-pro.impultienda.ar/checkout'
export const PURCHASE_ANCHOR = '#offer'
export const META_PIXEL_ID = '998827442617340'

export const BOX_IMAGE =
  'https://cdn.magicpatterns.com/uploads/ncRWXtqoz5diPXq2LqVgpg/Diseno-sin-titulo-13.webp'

export const insideImages: InsideImage[] = [
  {
    src: 'https://cdn.magicpatterns.com/uploads/fmHuvCQcGkHPHxhGECfTMm/Captura-de-pantalla-2026-10-04-a-la_s_-5.10.06_p._m..png',
    alt: 'Fault path: commercial oven — no heat, with numbered components and possible cause, check and next step',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/3oV3sZ6ZkHiLD2JEQzYLN2/Captura-de-pantalla-2026-10-04-a-la_s_-5.11.00_p._m..png',
    alt: 'Symptom: motor-driven component is inactive — step-by-step motor diagnostic flow',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/p5fQ23HC92PsrUkwQjPV3o/Captura-de-pantalla-2026-10-04-a-la_s_-5.11.17_p._m..png',
    alt: 'Don’t condemn the wrong side of the circuit — trace backward versus evaluate component',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/j3c77RMKquafcE1Znb8Q3D/Captura-de-pantalla-2026-10-04-a-la_s_-5.10.22_p._m..png',
    alt: 'Fault path: uneven cooking / uneven heat — hot and cool zones with diagnostic areas to check',
  },
  {
    src: 'https://cdn.magicpatterns.com/uploads/mNNG8szqaMX3BNEJedaZ91/Captura-de-pantalla-2026-10-04-a-la_s_-5.10.38_p._m..png',
    alt: 'Symptom: no water enters the machine — commercial dishwasher fill diagnostic path',
  },
]

export const painPoints: PainPoint[] = [
  {
    title: '😰 ONE SYMPTOM. TOO MANY POSSIBLE CAUSES.',
    body: 'Is it the power supply, thermostat, sensor, relay, heating element, ignition system, motor, pump, controller, wiring, or safety circuit? Finding the actual cause is the hard part.',
  },
  {
    title: '⚠️ YOU KNOW THE EQUIPMENT—BUT WHAT SHOULD YOU CHECK NEXT?',
    body: 'You understand the components. The challenge is knowing what to inspect first, what to test next, and how to narrow down the fault without wasting time.',
  },
  {
    title: '📚 YOUR DIAGNOSTIC RESOURCES ARE EVERYWHERE',
    body: "Manuals, wiring diagrams, technical PDFs, videos, fault codes, manufacturer guides and websites. The information exists—but it's scattered when you need it most.",
  },
  {
    title: '😩 STOP REPLACING PARTS BEFORE CONFIRMING THE FAULT',
    body: "A suspected component isn't always the root cause. Follow a structured process to isolate and confirm the problem before deciding what actually needs attention.",
  },
]

export const modules: Module[] = [
  {
    number: 1,
    title: 'COMMERCIAL COOKING EQUIPMENT DIAGNOSTICS™',
    body: 'Diagnose ovens, ranges, fryers, griddles, grills and steamers with structured workflows for heating, temperature, burner, fan, thermostat, sensor and control faults.',
  },
  {
    number: 2,
    title: 'ELECTRICAL & CONTROL SYSTEM DIAGNOSTICS™',
    body: 'Troubleshoot power supplies, wiring, switches, relays, contactors, transformers, motors, solenoids, heating elements, sensors, controllers and safety circuits.',
  },
  {
    number: 3,
    title: 'GAS, IGNITION & HEATING SYSTEM DIAGNOSTICS™',
    body: 'Follow structured diagnostic paths for burners, pilots, ignition systems, gas valves, flame sensors, thermocouples, heating faults, safety controls and lockout conditions.',
  },
  {
    number: 4,
    title: 'COMMERCIAL REFRIGERATION & COOLING DIAGNOSTICS™',
    body: 'Diagnose cooling, airflow, temperature, fan, compressor-control, sensor, defrost, icing and cycling problems across common commercial refrigeration equipment.',
  },
  {
    number: 5,
    title: 'DISHWASHING, WATER & PUMP SYSTEM DIAGNOSTICS™',
    body: 'Troubleshoot commercial dishwashers through filling, heating, washing, rinsing and draining systems, including pumps, valves, heaters, sensors and controls.',
  },
  {
    number: 6,
    title: 'SYMPTOM-BASED FAULT-FINDING SYSTEM™',
    body: 'Start with the symptom—no power, no heat, no ignition, poor cooling, leaks, drainage problems, abnormal noise or intermittent operation—and follow a structured Symptom → Cause → Test → Result → Next Step diagnostic path.',
  },
]

export const bonuses: Bonus[] = [
  {
    number: 1,
    title: 'COMMERCIAL KITCHEN DIAGNOSTIC QUICK-REFERENCE HANDBOOK™',
    body: 'Quickly access common symptoms, possible causes, first checks, diagnostic sequences and next-step references during service calls.',
  },
  {
    number: 2,
    title: 'TECHNICIAN TESTING & MEASUREMENT FIELD GUIDE™',
    body: 'Use structured references for electrical measurements, component testing, sensors, motors, relays, contactors, heating elements and controls.',
  },
  {
    number: 3,
    title: 'SERVICE CALL INSPECTION & DIAGNOSTIC WORKSHEETS™',
    body: 'Organize complaints, observations, tests, measurements, findings, confirmed faults and final verification with ready-to-use professional worksheets.',
  },
  {
    number: 4,
    title: 'INTERMITTENT & HARD-TO-FIND FAULT DIAGNOSTIC GUIDE™',
    body: 'Track faults that appear only under certain temperatures, loads, cycles or operating conditions and compare normal operation against failed operation.',
  },
  {
    number: 5,
    title: 'PREVENTIVE INSPECTION & EARLY FAULT DETECTION SYSTEM™',
    body: 'Identify and document developing problems, abnormal conditions and early warning signs before they become larger equipment issues.',
  },
]

export const differentiators: Differentiator[] = [
  {
    icon: 'wrench',
    title: 'Practical diagnostic references',
    body: 'Find the symptom, component, test or troubleshooting path you need.',
  },
  {
    icon: 'search',
    title: 'Structured fault-finding system',
    body: 'Move from symptoms and possible causes to checks, tests and fault isolation.',
  },
  {
    icon: 'zap',
    title: '6 dedicated diagnostic areas',
    body: 'Cooking equipment, electrical controls, heating, refrigeration, dishwashing and symptom-based diagnostics.',
  },
  {
    icon: 'gift',
    title: '5 practical professional bonuses',
    body: 'Quick references, testing guides, service worksheets, difficult-fault diagnostics and preventive inspection tools.',
  },
  {
    icon: 'refresh',
    title: 'Ready to use again and again',
    body: 'Return to the system whenever commercial kitchen equipment develops another fault.',
  },
]

export const offerBullets = [
  '6 comprehensive diagnostic modules',
  '5 strategic professional bonuses',
  'Cooking equipment diagnostics',
  'Electrical & control fault finding',
  'Gas, ignition & heating diagnostics',
  'Refrigeration & cooling diagnostics',
  'Dishwashing & water-system diagnostics',
  'Symptom-based fault-finding workflows',
]

export const testimonials: Testimonial[] = [
  {
    name: 'Mark Reynolds, 46',
    role: 'Commercial Equipment Service Technician',
    location: 'Leeds, United Kingdom',
    headline: 'A much clearer way to approach a fault.',
    quote:
      'Instead of jumping between different possibilities, I can start with the symptom and work through the checks in a logical order. It makes the diagnostic process much easier to organize.',
  },
  {
    name: 'Daniel Ortiz, 38',
    role: 'Restaurant Equipment Technician',
    location: 'Phoenix, United States',
    headline: 'The symptom-based section is extremely practical.',
    quote:
      'The same symptom can come from several different areas. Having the possible causes and testing path organized in one place gives me a much better starting point.',
  },
  {
    name: 'Sarah McAllister, 34',
    role: 'Field Service Technician',
    location: 'Calgary, Canada',
    headline: 'Useful during actual service calls.',
    quote:
      "I like that I can quickly find the system I'm working on, review the diagnostic path and then verify the exact specifications against the equipment documentation.",
  },
  {
    name: 'Greg Hollis, 52',
    role: 'Commercial Kitchen Maintenance Technician',
    location: 'Perth, Australia',
    headline: 'The worksheets keep everything organized.',
    quote:
      'Being able to record the complaint, tests, measurements and confirmed fault makes service calls easier to document and gives me something useful to reference later.',
  },
  {
    name: 'Ryan Fletcher, 41',
    role: 'Equipment Repair Professional',
    location: 'Chicago, United States',
    headline: 'Focused on diagnosis, not guessing.',
    quote:
      'What I like most is the emphasis on confirming the fault instead of immediately replacing the component that seems most obvious.',
  },
]

export const faqs: Faq[] = [
  {
    q: 'Who is this system for?',
    a: 'Commercial kitchen equipment technicians, restaurant equipment repair technicians, maintenance technicians, field service professionals and service contractors.',
  },
  {
    q: 'What equipment does it cover?',
    a: 'Commercial ovens, fryers, ranges, griddles, grills, steamers, refrigeration equipment, dishwashers, heating systems, pumps, motors, electrical controls, ignition systems and related equipment.',
  },
  {
    q: 'Is this for household appliance repair?',
    a: 'No. The system is specifically focused on commercial kitchen equipment.',
  },
  {
    q: 'Is this a physical product?',
    a: 'No. KITCHEN EQUIPMENT DIAGNOSTIC PRO™ is a 100% digital resource.',
  },
  {
    q: 'Is this a training course or certification?',
    a: 'No. It is a professional diagnostic and reference system.',
  },
  {
    q: 'Is it another theory-heavy ebook?',
    a: 'No. It focuses on practical references, troubleshooting workflows, component checks, testing and structured fault isolation.',
  },
  {
    q: 'Does this replace manufacturer service information?',
    a: 'No. Equipment-specific specifications and procedures should always be verified against the applicable manufacturer documentation.',
  },
  {
    q: 'How will I receive it?',
    a: "You'll receive immediate digital access after purchase.",
  },
  {
    q: 'What devices can I use it on?',
    a: 'Any device that opens PDF files — phone, tablet, laptop or desktop. Keep it on your phone during service calls, or print the worksheets and references you use most.',
  },
  {
    q: "What if it isn't right for me?",
    a: "You're covered by the 7-Day Money-Back Guarantee. If it isn't right for you, contact us within 7 days for a full refund according to the refund terms.",
  },
]

export const popupSales: PopupSale[] = [
  { name: 'James W.', city: 'Manchester', country: 'United Kingdom' },
  { name: 'Kevin L.', city: 'Toronto', country: 'Canada' },
  { name: 'Carlos M.', city: 'Dallas', country: 'United States' },
  { name: 'Brett H.', city: 'Melbourne', country: 'Australia' },
  { name: 'Tom R.', city: 'Glasgow', country: 'United Kingdom' },
  { name: 'Marc D.', city: 'Montreal', country: 'Canada' },
  { name: 'Jason P.', city: 'Atlanta', country: 'United States' },
  { name: 'Lisa K.', city: 'Brisbane', country: 'Australia' },
  { name: 'Steven B.', city: 'Bristol', country: 'United Kingdom' },
  { name: 'Aaron S.', city: 'Edmonton', country: 'Canada' },
  { name: 'Mike T.', city: 'Denver', country: 'United States' },
  { name: 'Dean C.', city: 'Sydney', country: 'Australia' },
]
